import { createTheme } from '@mui/material/styles';

// Shares the warm "Cyber Garage" identity established in publicTheme.ts
// (terracotta/forest-green, Lora/Source Sans) so the authenticated product
// and the marketing site read as one brand rather than two. Kept as a
// separate theme object rather than merged into publicTheme because the
// product's dense, data-heavy screens (tables, stat grids, sidebars) want
// calmer component defaults than the marketing site's editorial cards -
// no hover-lift transforms, a solid AppBar instead of a blurred one.
export const theme = createTheme({
  palette: {
    primary: {
      main: '#B4552F', // Terracotta - primary actions
      light: '#C97951',
      dark: '#8F4224',
      contrastText: '#FBF6EC',
    },
    secondary: {
      main: '#2E5339', // Forest green - links, secondary actions
      light: '#4A7052',
      dark: '#1F3B28',
      contrastText: '#FBF6EC',
    },
    error: {
      main: '#A63A3A',
    },
    success: {
      main: '#2E5339',
    },
    background: {
      default: '#FBF6EC', // Cream
      paper: '#FFFDF7',
    },
    text: {
      primary: '#2E2A22', // Ink
      secondary: '#5C5245',
    },
    divider: '#E7DBC0',
    grey: {
      100: '#FBF6EC',
      200: '#F0E9D8',
      300: '#E7DBC0',
      400: '#C9BC9E',
      500: '#8C816E',
      600: '#5C5245',
      700: '#453D33',
      800: '#332C24',
      900: '#2E2A22',
    },
  },
  typography: {
    fontFamily: '"Source Sans 3", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontFamily: '"Lora", Georgia, serif',
      fontSize: '2.5rem',
      fontWeight: 600,
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontFamily: '"Lora", Georgia, serif',
      fontSize: '2rem',
      fontWeight: 600,
      letterSpacing: '-0.00833em',
    },
    h3: {
      fontFamily: '"Lora", Georgia, serif',
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontFamily: '"Lora", Georgia, serif',
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontFamily: '"Lora", Georgia, serif',
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Lora", Georgia, serif',
      fontSize: '1rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
    button: {
      fontFamily: '"Source Sans 3", sans-serif',
      fontWeight: 600,
      fontSize: '0.875rem',
      letterSpacing: '0.02857em',
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 20px',
        },
        containedPrimary: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #E7DBC0',
          boxShadow: '0 1px 6px rgba(46, 42, 34, 0.05)',
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 0 rgba(46, 42, 34, 0.08)',
          backgroundColor: '#FFFDF7',
          color: '#2E2A22',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 6px rgba(46, 42, 34, 0.05)',
        },
        elevation2: {
          boxShadow: '0 2px 10px rgba(46, 42, 34, 0.08)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          padding: '8px 16px',
          fontSize: '0.875rem',
        },
      },
    },
  },
});

declare module '@mui/material/styles' {
  interface Theme {
    status: {
      danger: string;
    };
  }
  interface ThemeOptions {
    status?: {
      danger?: string;
    };
  }
}
